import {
  getGetInterviewTypeQueryKey,
  getGetInterviewTypeQueryOptions,
  useCreateInterviewTypeVersion,
  useGetOrgLogo,
  useListExtractions,
  useListRubrics,
  useUpdateInterviewType,
  type ExtractionListItemDto,
  type RubricListItemDto,
} from '@sara/api-client';
import { useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { toast } from '@sara/ui/components/sonner';
import { useActiveOrganization, useOrgSlug } from '../../../auth';
import {
  CreateInterviewTypeVersionForm,
  type CreateInterviewTypeVersionFormValues,
  type ExtraSelectItem,
  type SelectorOption,
} from '../../components/CreateInterviewTypeVersionForm';
import { SHOW_ORG_LOGO_TO_API, showOrgLogoFromApi } from '../../lib/show-org-logo';
import { consentConfigFromApi, consentConfigToApi } from '../../lib/consent-fields';
import { editAgentPromptFieldsForSubmit } from '../../lib/agent-prompt-submit';
import { toSuspense } from '../../../../lib/query';
import { getApiErrorMessage } from '../../../../lib/api-error';
import type { CreateInterviewTypeVersionFormContainerProps } from './types';

export function CreateInterviewTypeVersionFormContainerContent({
  interviewTypeId,
}: CreateInterviewTypeVersionFormContainerProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const orgSlug = useOrgSlug();
  const { organization } = useActiveOrganization();
  const orgId = organization?.id;

  const { data: logo } = useGetOrgLogo({ orgId: orgId ?? '' }, { query: { enabled: !!orgId } });
  // While the query is loading (`logo` is undefined) treat the org as having a
  // logo so the negative upload hint isn't shown prematurely; once resolved it
  // reflects the real `logoUrl`.
  const orgHasLogo = logo === undefined || logo.logoUrl !== null;

  const { data: interviewType } = useSuspenseQuery(
    toSuspense(getGetInterviewTypeQueryOptions(interviewTypeId))
  );
  const latestVersion = interviewType.versions[0] ?? null;

  const { data: extractionsData } = useListExtractions(
    { orgId: orgId ?? '' },
    { query: { enabled: !!orgId } }
  );
  const { data: rubricsData } = useListRubrics(
    { orgId: orgId ?? '' },
    { query: { enabled: !!orgId } }
  );

  const extractions: SelectorOption[] = (extractionsData ?? [])
    .filter((e: ExtractionListItemDto) => e.archivedAt === null)
    .map((e: ExtractionListItemDto) => ({
      id: e.id,
      name: e.name,
      latestVersionId: e.latestVersionId,
      latestVersion: e.latestVersion,
    }));

  const rubrics: SelectorOption[] = (rubricsData ?? [])
    .filter((r: RubricListItemDto) => r.archivedAt === null)
    .map((r: RubricListItemDto) => ({
      id: r.id,
      name: r.name,
      latestVersionId: r.latestVersionId,
      latestVersion: r.latestVersion,
    }));

  // Default to the pinned version from the current version being copied.
  const pinnedExtractionVersionId = latestVersion?.extraction?.extractionVersionId ?? null;
  const pinnedRubricVersionId = latestVersion?.rubric?.rubricVersionId ?? null;

  // If the pinned version is not the latest for its entity (or its parent is
  // archived and therefore absent from the offered list), include an extra
  // select item so the default remains selectable without being duplicated.
  const pinnedExtraction = latestVersion?.extraction;
  let extraExtractionItem: ExtraSelectItem | undefined;
  if (pinnedExtractionVersionId && pinnedExtraction) {
    const entity = extractions.find((e) => e.id === pinnedExtraction.extractionId);
    // Show the extra item when the offered entity is on a newer version, OR
    // when the parent is archived (entity missing from the non-archived list).
    if (!entity || entity.latestVersionId !== pinnedExtractionVersionId) {
      extraExtractionItem = {
        versionId: pinnedExtractionVersionId,
        label: `${pinnedExtraction.name} · v${pinnedExtraction.version} (current)`,
      };
    }
  }

  const pinnedRubric = latestVersion?.rubric;
  let extraRubricItem: ExtraSelectItem | undefined;
  if (pinnedRubricVersionId && pinnedRubric) {
    const entity = rubrics.find((r) => r.id === pinnedRubric.rubricId);
    if (!entity || entity.latestVersionId !== pinnedRubricVersionId) {
      extraRubricItem = {
        versionId: pinnedRubricVersionId,
        label: `${pinnedRubric.name} · v${pinnedRubric.version} (current)`,
      };
    }
  }

  const defaultValues: CreateInterviewTypeVersionFormValues = {
    // Prompt is pass-through: seeded from the template's live agent config (folded
    // into the detail GET). `promptLoaded` is true only when that live read
    // succeeded (non-null) — otherwise the '' defaults are untrustworthy and the
    // wipe-guard must not submit them.
    systemPrompt: interviewType.systemPrompt ?? '',
    firstMessage: interviewType.firstMessage ?? '',
    promptLoaded: interviewType.systemPrompt !== null,
    // 'none' is the sentinel for "no selection" (Radix Select doesn't allow empty string values)
    extractionVersionId: pinnedExtractionVersionId ?? 'none',
    rubricVersionId: pinnedRubricVersionId ?? 'none',
    candidateTitle: latestVersion?.title ?? '',
    candidateDescription: latestVersion?.description ?? '',
    candidateEndOfCallNotes: latestVersion?.endOfCallNotes ?? '',
    candidateDeclinedMessage: latestVersion?.declinedMessage ?? '',
    candidateShowOrgLogo: showOrgLogoFromApi(latestVersion?.showOrgLogo ?? null),
    // Pre-fill consent + secondary-link overrides so cutting a new version keeps
    // the current values unless edited (null = platform default, "" = hidden).
    consentConfig: consentConfigFromApi({
      consentStatement: latestVersion?.consentStatement ?? null,
      consentDetails: latestVersion?.consentDetails ?? null,
      secondaryLinkLabel: latestVersion?.secondaryLinkLabel ?? null,
      secondaryLinkDescription: latestVersion?.secondaryLinkDescription ?? null,
    }),
    // Pre-fill from the current version so cutting a new version keeps the
    // declared variables unless the user edits them.
    dynamicVariableDefinitions: (latestVersion?.dynamicVariableDefinitions ?? []).map((d) => ({
      key: d.key,
      name: d.name,
      type: d.type,
      required: d.required,
      description: d.description ?? '',
      allowedValues: d.allowedValues ?? [],
      defaultValue: d.defaultValue ?? '',
    })),
  };

  // Two writes on one Save, in this order:
  // 1) PATCH the live agent prompt (pass-through, wipe-guarded) via the update
  //    endpoint, sending the template's existing metadata unchanged so only the
  //    prompt can change (the server diffs vs live and no-ops if unchanged).
  // 2) Create the new immutable version (the versioned config). The prompt is
  //    independent of versions, so it is applied first; if version creation then
  //    fails, the live prompt is still correct and the error surfaces.
  const { mutateAsync: patchTemplateAsync, isPending: isPatching } = useUpdateInterviewType();
  const {
    mutateAsync: createVersionAsync,
    isPending: isCreatingVersion,
    error,
  } = useCreateInterviewTypeVersion();
  const isPending = isPatching || isCreatingVersion;

  async function handleSubmit(values: CreateInterviewTypeVersionFormValues) {
    try {
      // Only PATCH the agent when the live prompt actually loaded (wipe-guard):
      // never submit the '' defaults from a soft-failed read.
      const promptFields = editAgentPromptFieldsForSubmit(values);
      if (promptFields && interviewType.elevenlabsAgentId) {
        await patchTemplateAsync({
          id: interviewTypeId,
          data: {
            name: interviewType.name,
            slug: interviewType.slug,
            description: interviewType.description,
            elevenlabsAgentId: interviewType.elevenlabsAgentId,
            ...promptFields,
          },
        });
      }

      const created = await createVersionAsync({
        id: interviewTypeId,
        data: {
          agentPrompt: '',
          extractionVersionId:
            values.extractionVersionId && values.extractionVersionId !== 'none'
              ? values.extractionVersionId
              : undefined,
          rubricVersionId:
            values.rubricVersionId && values.rubricVersionId !== 'none'
              ? values.rubricVersionId
              : undefined,
          title: values.candidateTitle,
          description: values.candidateDescription,
          endOfCallNotes: values.candidateEndOfCallNotes,
          declinedMessage: values.candidateDeclinedMessage,
          showOrgLogo: SHOW_ORG_LOGO_TO_API[values.candidateShowOrgLogo],
          ...consentConfigToApi(values.consentConfig),
          dynamicVariableDefinitions: values.dynamicVariableDefinitions.length
            ? values.dynamicVariableDefinitions.map((d) => ({
                key: d.key,
                name: d.name,
                type: d.type,
                required: d.required,
                allowedValues: d.allowedValues,
                ...(d.description.trim() ? { description: d.description.trim() } : {}),
                ...(d.defaultValue.trim() ? { defaultValue: d.defaultValue.trim() } : {}),
              }))
            : undefined,
        },
      });

      void queryClient.invalidateQueries({
        queryKey: getGetInterviewTypeQueryKey(interviewTypeId),
      });
      toast.success(`Created version v${created.version}`);
      void navigate({
        to: '/orgs/$orgSlug/templates/$interviewTypeId',
        params: { orgSlug, interviewTypeId },
      });
    } catch (err) {
      toast.error('Failed to save template', { description: getApiErrorMessage(err) });
    }
  }

  return (
    <div className="space-y-6">
      {error ? (
        <div className="whitespace-pre-line rounded-xl border border-destructive/40 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          {getApiErrorMessage(error) ?? 'Failed to create version'}
        </div>
      ) : null}
      <CreateInterviewTypeVersionForm
        templateName={interviewType.name}
        templateDescription={interviewType.description}
        elevenlabsAgentId={interviewType.elevenlabsAgentId}
        defaultValues={defaultValues}
        orgHasLogo={orgHasLogo}
        extractions={extractions}
        rubrics={rubrics}
        extraExtractionItem={extraExtractionItem}
        extraRubricItem={extraRubricItem}
        onSubmit={handleSubmit}
        onCancel={() =>
          navigate({
            to: '/orgs/$orgSlug/templates/$interviewTypeId',
            params: { orgSlug, interviewTypeId },
          })
        }
        isPending={isPending}
      />
    </div>
  );
}
